import { useState, useRef, useEffect, useId } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Settings2,
  Video,
  ChevronDown,
  KeyRound,
  Film,
  Shield,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import "@/styles/account-redesign.css";

interface UserMenuProps {
  mobile?: boolean;
  onAction?: () => void;
}

export function UserMenu({ mobile = false, onAction }: UserMenuProps) {
  const { user, logout, isLoading } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const navigate = useNavigate();
  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [isOpen]);
  const close = () => {
    setIsOpen(false);
    onAction?.();
  };
  const handleLogout = async () => {
    close();
    await logout();
    navigate("/");
  };
  if (isLoading)
    return (
      <div
        className="bc-user-placeholder"
        aria-label="Loading account"
        role="status"
      />
    );
  if (!user)
    return (
      <div className={`bc-user-entry ${mobile ? "w-full" : ""}`}>
        <Link
          to="/login"
          onClick={close}
          className="bc-button bc-button-ghost text-xs"
        >
          Sign in
        </Link>
        {mobile && (
          <Link
            to="/register"
            onClick={close}
            className="bc-button bc-button-primary text-xs"
          >
            Get started
          </Link>
        )}
      </div>
    );

  const avatar = user.avatarUrl ? (
    <img src={user.avatarUrl} alt="" className="bc-avatar" />
  ) : (
    <div className="bc-avatar" aria-hidden="true">
      {user.username.charAt(0).toUpperCase()}
    </div>
  );
  const links = [
    { to: "/my-replays", label: "My replays", icon: Video },
    { to: "/my-clips", label: "My clips", icon: Film, tag: "Create" },
    { to: "/profile", label: "Account settings", icon: Settings2 },
    { to: "/settings/tokens", label: "API access", icon: KeyRound },
    ...(user.isAdmin
      ? [{ to: "/admin", label: "Administration", icon: Shield }]
      : []),
  ];
  const panel = (
    <>
      <div className="bc-user-identity">
        {avatar}
        <div>
          <strong>{user.username}</strong>
          <p>{user.email}</p>
        </div>
      </div>
      <nav className="bc-user-links" aria-label="Your account">
        {links.map(({ to, label, icon: Icon, ...rest }) => (
          <Link key={to} to={to} onClick={close}>
            <Icon size={16} />
            {label}
            {"tag" in rest && rest.tag && <small>{rest.tag}</small>}
          </Link>
        ))}
      </nav>
      <button onClick={handleLogout} className="bc-user-signout">
        <LogOut size={16} /> Sign out
      </button>
    </>
  );
  if (mobile) return <div>{panel}</div>;
  return (
    <div
      className="relative"
      ref={menuRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setIsOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={`Account menu for ${user.username}`}
        className="bc-user-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        {avatar}
        <span className="hidden sm:inline">{user.username}</span>
        <ChevronDown size={13} className={isOpen ? "rotate-180" : ""} />
      </button>
      {isOpen && (
        <div id={panelId} className="bc-user-popover">
          {panel}
        </div>
      )}
    </div>
  );
}
